import { useEffect, useLayoutEffect } from 'react'
import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query'

import InactivityHandler from '@shared/components/src/InactivityHandler'
import { TrackUserActivity } from '@shared/components/src/KeepSessionAlive'
import useCurrentUserId from '@shared/hooks/src/useCurrentUserId'
import DropdownOptionsProvider from '@shared/providers/src/DropdownOptionsProvider'
import FeatureFlagsProvider from '@shared/providers/src/FeatureFlagsProvider'
import { useMe } from '@shared/providers/src/MeProvider'
import SettingsProvider from '@shared/providers/src/SettingsProvider'
import API from '@shared/services/src/API'
import { QK, UserRole } from '@shared/utils'

import QPhrasesProvider from '@providers/QPhrasesProvider'
import useMigrateUserSettings from '@hooks/useMigrateUserSettings'
import Layout from '@components/Layout'
import Notifications from '@components/Notifications'
import QPhrasesInputHandler from '@components/QPhrasesInputHandler'
import { inactivityTime } from '@config'

import LayoutStateProvider from '../LayoutStateProvider'
import MeSuspenseProvider from '../MeProvider'
import VoiceWidgetStateProvider from '../VoiceWidgetStateProvider'
import useCacheRefresh from './AuthenticatedProviders.hooks'

export default function AuthenticatedProviders() {
  useCacheRefresh()

  const userId = useCurrentUserId()
  if (!userId) return null
  return <HeartbeatGuard />
}

function HeartbeatGuard() {
  useSuspenseQuery({
    queryKey: QK.heartbeat.check,
    queryFn: () => API.heartbeat.check(null, undefined),
    staleTime: 0,
  })

  return <DataGuard />
}

function DataGuard() {
  const userId = useCurrentUserId()

  useSuspenseQueries({
    queries: [
      {
        queryKey: QK.me(userId).details,
        queryFn: () => API.me.details(),
      },
      {
        queryKey: QK.platform.constants.list,
        queryFn: () => API.platform.constants.list(),
      },
      {
        queryKey: QK.platform.settings.list,
        queryFn: () => API.platform.settings.list(),
      },
      {
        queryKey: QK.platform.features.list,
        queryFn: () => API.platform.features.list(),
      },
    ],
  })

  return (
    <DropdownOptionsProvider>
      <SettingsProvider>
        <FeatureFlagsProvider>
          <MeSuspenseProvider>
            <QPhrasesProvider>
              <PatientGuard />
            </QPhrasesProvider>
          </MeSuspenseProvider>
        </FeatureFlagsProvider>
      </SettingsProvider>
    </DropdownOptionsProvider>
  )
}

function PatientGuard() {
  const me = useMe()
  const isPatient = me.role === UserRole.Patient

  useEffect(() => {
    if (isPatient) {
      window.location = `${import.meta.env.VITE_PATIENT_DOMAIN}/app`
    }
  }, [isPatient])

  if (isPatient) return null
  return <Application />
}

function Application() {
  const me = useMe()

  useMigrateUserSettings()

  // Capture timezone for providers
  useLayoutEffect(() => {
    if (me.role === UserRole.Provider && me.provider.timezone) {
      window.timezone = me.provider.timezone
    }
    return () => {
      window.timezone = undefined
    }
  }, [me])

  return (
    <>
      <VoiceWidgetStateProvider>
        <LayoutStateProvider>
          <Layout />
        </LayoutStateProvider>
      </VoiceWidgetStateProvider>
      <Notifications />
      <QPhrasesInputHandler />
      <TrackUserActivity />
      <InactivityHandler interval={inactivityTime} />
    </>
  )
}
